/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, type ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';
import { format } from 'date-fns';

import {
	type BlogMeta,
	BrandHeading,
	Card,
	HeadingContextProvider,
	Image,
	LocalNav,
} from '@af/design-system-docs-ui';
import Heading from '@atlaskit/heading';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import OverviewPage from '../../templates/overview-page';

import heroDark from './_assets/hero.dark.png';
import heroLight from './_assets/hero.light.png';
import { meta as atlassianUiRefreshMeta } from './atlassian-ui-refresh-updates';
import { meta as buildingAtlassiansNewIconSystemMeta } from './building-atlassians-new-icon-system';
import { meta as buttonAndIconMeta } from './evolving-button-and-links';
import { meta as implementingTypographyAtScaleMeta } from './implementing-typography-at-scale';
import { meta as typographyGaMeta } from './new-typography-in-general-availability';
import { meta as typographyMeta } from './typography-and-iconography-updates';

export const blogs = [
	buildingAtlassiansNewIconSystemMeta,
	typographyGaMeta,
	atlassianUiRefreshMeta,
	typographyMeta,
	buttonAndIconMeta,
	implementingTypographyAtScaleMeta,
];

const featured = atlassianUiRefreshMeta;

const sectionStyles = css({
	display: 'flex',
	gap: token('space.200'),
	flexDirection: 'column',
});

const cardGridStyles = xcss({
	display: 'grid',
	height: '100%',
	gridTemplateColumns: '1fr',
	'@media (min-width: 48rem)': {
		gridTemplateColumns: '1fr 1fr',
	},
});

const imageStyles = xcss({
	height: '100%',
	minHeight: '150px',
	// background image dynamically set in `style` tag
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	backgroundPosition: '50% 50%',
});

const Section = ({ children }: { children: ReactNode }) => (
	<section css={sectionStyles}>{children}</section>
);

const groupByYear = (buckets: { [id: string]: BlogMeta[] }, blog: BlogMeta) => {
	const year = blog.publishDate.getFullYear();

	if (!buckets[year]) {
		buckets[year] = [];
	}

	buckets[year].push(blog);

	return buckets;
};

const sortByDate = (a: BlogMeta, b: BlogMeta) => b.publishDate.getTime() - a.publishDate.getTime();

export default () => {
	const blogsSorted = blogs.sort(sortByDate);
	const blogsByYear = blogsSorted.reduce(groupByYear, {});

	const sortedYears = Object.keys(blogsByYear).sort().reverse();

	return (
		<HeadingContextProvider>
			<OverviewPage
				title="What's new"
				categorySlug="whats-new"
				description="Release roundups, feature deep dives, and more."
				image={{
					light: heroLight,
					dark: heroDark,
				}}
				entries={[]}
			>
				<Inline space="space.500" alignBlock="stretch">
					<Stack space="space.800">
						<Section>
							<BrandHeading as="h2" id="featured" size="xlarge">
								Featured article
							</BrandHeading>
							<Card borderColor="color.border" href={featured.href}>
								<Image src={featured.image} alt="" />
								<Box padding="space.300">
									<Stack space="space.050">
										<Heading size="medium">{featured.title}</Heading>
										<Text maxLines={3}>{featured.description}</Text>
										<Box paddingBlockStart="space.150">
											<Inline space="space.050" alignBlock="center">
												<Bleed>
													<Tag text={featured.type} />
												</Bleed>
												<Inline space="space.050" separator={<Text>·</Text>}>
													<Text>{featured.readTime}</Text>
													<Text>{format(featured.publishDate, 'LLL d, yyyy')}</Text>
												</Inline>
											</Inline>
										</Box>
									</Stack>
								</Box>
							</Card>
						</Section>
						<Section>
							<BrandHeading as="h2" size="xlarge">
								All articles
							</BrandHeading>
							{sortedYears.map((year) => (
								<Fragment key={year}>
									<BrandHeading id={`${year}`} as="h3" size="large">
										{year}
									</BrandHeading>
									<Stack space="space.100">
										{blogsByYear[`${year}`].map((meta: BlogMeta) => (
											<Card borderColor="color.border" key={meta.href} href={meta.href}>
												<Box xcss={cardGridStyles}>
													<Box
														style={{
															backgroundImage: `url(${meta.image})`,
														}}
														xcss={imageStyles}
													></Box>
													<Box padding="space.300">
														<Stack space="space.050">
															<Heading size="small">{meta.title}</Heading>
															<Text>{meta.description}</Text>
															<Box paddingBlockStart="space.150">
																<Inline space="space.050" alignBlock="center">
																	<Bleed>
																		<Tag text={meta.type} />
																	</Bleed>
																	<Inline space="space.050" separator={<Text>·</Text>}>
																		<Text>{meta.readTime}</Text>
																		<Text>{format(meta.publishDate, 'LLL d, yyyy')}</Text>
																	</Inline>
																</Inline>
															</Box>
														</Stack>
													</Box>
												</Box>
												{meta.href.startsWith('http') && (
													<Box xcss={externalIconStyles}>
														<LinkExternalIcon label="" color={token('color.icon')} />
													</Box>
												)}
											</Card>
										))}
									</Stack>
								</Fragment>
							))}
						</Section>
					</Stack>
					<Show above="lg">
						<LocalNav />
					</Show>
				</Inline>
			</OverviewPage>
		</HeadingContextProvider>
	);
};

const bleedStyles = xcss({
	marginInlineStart: 'space.negative.050',
});

const Bleed = ({ children }: { children: ReactNode }) => {
	return <Box xcss={bleedStyles}>{children}</Box>;
};

const externalIconStyles = xcss({
	position: 'absolute',
	insetBlockStart: 'space.200',
	insetInlineEnd: 'space.200',
});
